import React, { useEffect, useState } from 'react';
import api from '@api';
import BracketDisplay from '../Auth/BracketCreation/Core/BracketDisplay.jsx'; 
import { useLocation, useNavigate } from 'react-router-dom';
import './TournamentBracket.css'
import { useDomain } from '@useDomain';
import { getUpdatedBracketWithWinners } from "../Auth/BracketCreation/Utils/bracketUtils.js";



function TournamentBracket({ page, organization }) {
  const pageSettingsContent =
    page?.content || {};
  const [tournamentSeasons, setTournamentSeasons] = useState([]);
  const [tournamentDivisions, setTournamentDivisions] = useState({});
  const [expandedSeasonId, setExpandedSeasonId] = useState(null);
  const bracketTitle =
    pageSettingsContent.bracket_title || "Tournament Bracket";

  const backgroundImageUrl =
    pageSettingsContent.background_image_url || "";
  const backgroundColor =
    pageSettingsContent.background_color || "#111111";

  const titleColor =
    pageSettingsContent.title_color || "#ffffff";
  const [division, setDivision] = useState(null);
  const [bracketData, setBracketData] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const { slug } = useDomain();
  const query = useQueryParams();
  const seasonId = query.get('seasonId');
  const divisionId = query.get('divisionId');
  const navigate = useNavigate()
  const location = useLocation();
  // Fetch division & teams
  const backgroundStyle = {
    backgroundColor,
    ...(backgroundImageUrl
      ? {
        backgroundImage: `url("${backgroundImageUrl}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
      : {}),
  };
  useEffect(() => {
    if (seasonId && divisionId) return;
    if (!organization?.id) return;

    let alive = true;

    const fetchTournamentOptions = async () => {
      try {
        const seasonsRes = await api.get(
          `/api/seasonsv2/tournament?organization_id=${organization.id}`
        );

        const nextSeasons = seasonsRes.data?.seasons || [];

        if (!alive) return;

        setTournamentSeasons(nextSeasons);

        const nextDivisions = {};

        for (const season of nextSeasons) {
          const divisionsRes = await api.get(
            `/api/seasonsv2/${season.id}/divisions`
          );

          if (
            Array.isArray(divisionsRes.data) &&
            divisionsRes.data.length > 0
          ) {
            nextDivisions[season.id] = divisionsRes.data;
          }
        }

        if (!alive) return;

        setTournamentDivisions(nextDivisions);
      } catch (err) {
        console.error(
          "Error fetching tournament division options:",
          err
        );
      }
    };

    fetchTournamentOptions();

    return () => {
      alive = false;
    };
  }, [
    organization?.id,
    seasonId,
    divisionId,
  ]);

  useEffect(() => {
    if (!seasonId || !divisionId) return;
    const fetchDivision = async () => {
      try {
        const res = await api.get(`/api/seasonsv2/${seasonId}/divisions/${divisionId}`, {
          withCredentials: true,
        });
        // Sort teams by position if available (for visual order)
        const sortedDivision = {
          ...res.data,
          teams: [...(res.data.teams || [])].sort(
            (a, b) => (a.position ?? 999) - (b.position ?? 999)
          ),
        };
        setDivision(sortedDivision);
      } catch (err) {
        console.error("Error fetching division:", err);
      }
    };
    fetchDivision();
  }, [seasonId, divisionId]);


  function useQueryParams() {
    const { search } = useLocation();
    return new URLSearchParams(search);
  }


  // Fetch games
  useEffect(() => {
    if (!seasonId || !divisionId) return;
    const fetchGames = async () => {
      try {
        const res = await api.get(`/api/seasonsv2/${seasonId}/divisions/${divisionId}/games`, {
          withCredentials: true,
        });
        setGames(res.data);
      } catch (err) {
        console.error("Error fetching division games:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, [seasonId, divisionId]);


  const hasValidBracket = (json) => {
    if (!json) return false;
    try {
      const parsed = typeof json === "string" ? JSON.parse(json) : json;
      return parsed?.rounds && parsed.rounds.length > 0;
    } catch {
      return false;
    }
  };
  useEffect(() => {
    if (!division) return;

    const valid = hasValidBracket(division.bracket_json);

    if (valid) {
      try {
        const parsed =
          typeof division.bracket_json === "string"
            ? JSON.parse(division.bracket_json)
            : division.bracket_json;

        const roundsWithWinners = getUpdatedBracketWithWinners(parsed, games);

        setBracketData({
          ...parsed,
          rounds: roundsWithWinners,
        });
      } catch (err) {
        console.error("❌ Error parsing saved bracket:", err);
      }
    } else {
      // ❗ NO VALID BRACKET → DO NOT GENERATE ANYTHING
      // Public page should show nothing, or a message instead
      setBracketData(null);
    }

  }, [division, games]);



  if (!seasonId || !divisionId) {
    return (
      <div
        className="fab_tournament_bracket_bg"
        style={backgroundStyle}
      >
        <div className="fab_tournament_bracket_wrapper">
          <h2
            className="bracket-public-title"
            style={{ color: titleColor }}
          >
            {bracketTitle}
          </h2>

          <div className="tournament-bracket-selector">
            <p className="tournament-bracket-selector-text">
              Select a tournament division to view its bracket.
            </p>

            <div className="tournament-bracket-selector-list">
              {tournamentSeasons.map((season) => {
                const seasonDivisions =
                  tournamentDivisions[season.id] || [];

                if (!seasonDivisions.length) return null;

                const isExpanded =
                  expandedSeasonId === season.id;

                return (
                  <div
                    key={season.id}
                    className="tournament-bracket-selector-season"
                  >
                    <button
                      type="button"
                      className={`tournament-bracket-selector-season-btn ${isExpanded ? "is-open" : ""
                        }`}
                      onClick={() =>
                        setExpandedSeasonId(
                          isExpanded ? null : season.id
                        )
                      }
                    >
                      <span>{season.name}</span>
                      <span className="tournament-bracket-selector-arrow">
                        {isExpanded ? "−" : "+"}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="tournament-bracket-selector-divisions">
                        {seasonDivisions.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className="tournament-bracket-selector-division-btn"
                            onClick={() =>
                              navigate(
                                `${location.pathname}?seasonId=${season.id}&divisionId=${item.id}`
                              )
                            }
                          >
                            {item.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Optionally: you can display a winner banner if you want, like admin
  // import DivisionWinnerBanner from './DivisionWinnerBanner.jsx'

  if (loading) {
    return (
      <div
        className="fab_tournament_bracket_bg"
        style={{
          backgroundImage: `url("${page?.content?.background_image_url}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          style={{
            color: "#fff",
            textAlign: "center",
            padding: "80px 20px",
          }}
        >
          Loading tournament bracket...
        </div>
      </div>
    );
  }

  return (
    <div
      className="fab_tournament_bracket_bg"
      style={{
        backgroundImage: `url("${page?.content?.background_image_url}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div
        className="fab_tournament_bracket_wrapper"
        style={{
          position: "relative",
          overflowX: "auto",   // enable horizontal scroll
          overflowY: "visible",
          paddingLeft: 40,     // breathing room!
          paddingRight: 40,    // breathing room!
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        <h2
          className="bracket-public-title"
          style={{ color: titleColor }}
        >
          {division?.name
            ? `${division.name} ${bracketTitle}`
            : bracketTitle}
        </h2>
        <div className='bracket-public-container'>

          <div style={{ position: "relative", zIndex: 2 }}>
            {bracketData ? (
              <BracketDisplay
                bracketData={bracketData}
                games={games}
                readOnly={true}
                colorScheme="yellow-black"
                navigate={navigate}
                slug={slug}
                gameDetailsTextColor="#fff"
                division={division}
              />
            ) : (
              <p
                style={{
                  color: "#fff",
                  textAlign: "center",
                  padding: "60px 20px",
                }}
              >
                No tournament bracket has been published yet.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>

  );
}

export default TournamentBracket;
